import React from 'react';

import { useIsMutating } from '@tanstack/react-query';
import classNames from 'classnames';
import PdfIcon from 'icons/ic-pdf.svg';
import LoadingIcon from 'icons/loading.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

const elementHeightClasses = ['tw-h-[218px]', 'tw-h-[343px]', 'tw-h-[727px]'];

export function AttachmentImage({
  currentAttachment,
  draggable,
  imageSrc,
  isPdf,
  isRotating,
  onPreview,
}) {
  const { t: i18nTCommon } = useTranslation('common');

  const isResizing =
    useIsMutating({
      mutationKey: ['updateAttachment'],
      predicate: (mutation) => {
        const {
          state: { variables },
        } = mutation;

        return (
          variables.hashid === currentAttachment.hashid &&
          Object.keys(variables.params || {}).includes('display_size')
        );
      },
    }) > 0;

  const sizeClass =
    elementHeightClasses[currentAttachment.displaySizeValue] || 'tw-h-[343px]';

  return (
    <div
      className={classNames(
        'tw-relative tw-w-full tw-overflow-hidden tw-rounded-xl tw-border-1 tw-border-solid tw-border-grey-200 tw-transition-opacity tw-duration-300',
        sizeClass,
      )}
    >
      {isPdf && (
        <div className='tw-absolute tw-right-2 tw-top-2 tw-z-10'>
          <PdfIcon />
          <span className='tw-sr-only'>{i18nTCommon('attachments.pdf')}</span>
        </div>
      )}
      <div className='tw-group'>
        <div>
          {(isResizing || isRotating || !imageSrc) && (
            <div className='tw-absolute tw-inset-0 tw-z-10 tw-flex tw-w-full tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-30'>
              <div className='tw-apply-loading-spinner--blue-light'>
                <LoadingIcon className='animated-spinner' />
                <span className='tw-sr-only'>
                  {i18nTCommon('shared.loading...')}
                </span>
              </div>
            </div>
          )}
          {imageSrc ?
            <div>
              <img
                alt='Attachment preview'
                className={classNames(
                  'tw-w-full tw-object-contain tw-shadow-none',
                  sizeClass,
                )}
                loading='lazy'
                src={imageSrc}
              />
            </div>
          : <div className='tw-bg-gray-200 tw-flex tw-h-[343px] tw-w-full tw-items-center tw-justify-center' />
          }
        </div>
        <div
          {...draggable?.attributes}
          className={classNames(
            'tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center hover:tw-bg-black hover:tw-bg-opacity-30',
            { 'tw-cursor-move': draggable },
          )}
          data-element-name='draggable-preview-container'
        >
          {/* prevents the preview button from being in the draggable area, so it can be clicked */}
          <div {...draggable?.listeners} className='tw-absolute tw-inset-0' />
          <button
            className='tw-absolute tw-z-10 tw-rounded-lg tw-border-1 tw-border-solid tw-bg-white tw-px-3 tw-py-2 tw-font-semibold tw-text-grey-900 tw-opacity-0 group-hover:tw-opacity-100'
            onClick={onPreview}
            type='button'
          >
            {i18nTCommon('shared.preview')}
          </button>
        </div>
      </div>
    </div>
  );
}

AttachmentImage.propTypes = {
  currentAttachment: PropTypes.object,
  draggable: PropTypes.shape({
    attributes: PropTypes.object.isRequired,
    listeners: PropTypes.object.isRequired,
  }),
  imageSrc: PropTypes.string,
  isPdf: PropTypes.bool,
  isRotating: PropTypes.bool,
  onPreview: PropTypes.func.isRequired,
};
